/**
 * Single source of truth for top-level scene IDs and the URL-id → backend
 * poi-filter id mapping. Used by:
 *   - `App.tsx` for `handleEnterScene` (MenuOverlay click)
 *   - `createSdkScene.ts` (`navigateToScene`) for the nav beacons on the `overall` root scene
 *
 * Keep this list in sync with `splatRegistry.ts` / route handling.
 */
export const KNOWN_SCENES: ReadonlySet<string> = new Set([
  'overall',
  'kaaba',
  'arafat',
  'mina',
  'muzdalifah',
]);

/**
 * Root-scene predicate — the top-down map. Every "am I on the root?" branch
 * (OverallSceneOverlay vs AssistantHud, menu-button gate, Main-map pill,
 * scene_index analytics) goes through it.
 */
export const isRootSceneId = (sceneId: string | null | undefined): boolean => sceneId === 'overall';

/**
 * URL scene id → backend scene id. Used both for POI filtering in
 * `$sceneSights` (which keys on `sight.sceneId`) and for the chat intro
 * lookup in `$introsMap` (keyed by backend scene id).
 *
 * `overall` maps to `'overall-scene'` so the assistant intro arrives even
 * though the scene has no backend sights — its POIs are static
 * `navigationPois` declared in `overall.ts` (the empty `$sceneSights`
 * filter is the reason `overall.ts` sets `disablePoiChips: true`).
 */
export const SCENE_POI_ID: Record<string, string> = {
  overall: 'overall-scene',
  kaaba: 'main',
  arafat: 'arafat-scene',
  mina: 'mina-scene',
  muzdalifah: 'muzdalifa-scene',
  // Era and inside scenes are NOT listed: their config id IS the backend id
  // (e.g. `kaaba-600ce`) — every lookup falls back via `?? id`. The exception
  // is an alias: the backend knows only the canonical id, so the alias would
  // find no POIs and fail `scene_open`.
  'kaaba-inside-scene': 'kaaba-inside',
};
